import { useEffect, useMemo } from 'react';

export const SUPPORTED_LOCALES = ['en', 'pt-br'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'promptilia-locale';
const DEFAULT_LOCALE: SupportedLocale = 'en';

function isLocale(value: string | null): value is SupportedLocale {
  return value !== null && SUPPORTED_LOCALES.includes(value as SupportedLocale);
}

function detectLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const url = new URL(window.location.href);
  const pathSegment = url.pathname.split('/').filter(Boolean)[0] || null;
  if (isLocale(pathSegment)) return pathSegment;

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  const navLang = navigator.language.toLowerCase();
  if (navLang.startsWith('pt')) return 'pt-br';

  return DEFAULT_LOCALE;
}

export function useLanguage() {
  const locale = useMemo(() => detectLocale(), []);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasLocalePrefix = /^\/(en|pt-br)(\/|$)/i.test(window.location.pathname);
    if (hasLocalePrefix) return;
    const path = window.location.pathname === '/' ? '' : window.location.pathname;
    const nextPath = `/${locale}${path}`;
    const nextUrl = `${nextPath}${window.location.search}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }, [locale]);

  const translatePath = (input: string) => {
    const clean = input.replace(/^\/(en|pt-br)/i, '');
    const normalized = clean.startsWith('/') ? clean : `/${clean}`;
    return `/${locale}${normalized === '/' ? '' : normalized}` || `/${locale}`;
  };

  const setLocale = (next: SupportedLocale) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, next);
    const path = window.location.pathname.replace(/^\/(en|pt-br)/i, '');
    const newPath = `/${next}${path.startsWith('/') ? path : `/${path}`}`;
    window.location.href = newPath + window.location.search + window.location.hash;
  };

  return { locale, translatePath, setLocale } as const;
}
